import { useState } from 'react';
import type { DuelView } from '@tm/shared';
import { api } from '../lib/api.js';
import { shareLink } from '../lib/telegram.js';
import { trackGoal } from '../lib/metrika.js';

function myStatus(d: DuelView, me: number): 'my-turn' | 'waiting' | 'done' | 'no-rival' {
  const mine = d.challenger.userId === me ? d.challenger : d.opponent;
  const other = d.challenger.userId === me ? d.opponent : d.challenger;
  if (mine?.finishedAt == null) return 'my-turn';
  if (!other) return 'no-rival';
  if (other.finishedAt == null) return 'waiting';
  return 'done';
}

export function Duels({
  duels,
  meId,
  onPlay,
  onRefresh,
}: {
  duels: DuelView[];
  meId: number;
  onPlay: (duelId: string) => void;
  onRefresh: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.createDuel();
      trackGoal('duel_create');
      setLink(res.chatUrl);
      shareLink(res.chatUrl, 'Давай посоревнуемся в таблице умножения! Примеры у нас будут одинаковые.');
      onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать вызов');
    } finally {
      setBusy(false);
    }
  };

  const active = duels.filter((d) => d.status !== 'expired');

  return (
    <div className="stack">
      <h1>Дуэли</h1>

      <div className="card">
        <p className="small dim" style={{ marginTop: 0 }}>
          Вызови друга — вам достанутся одинаковые примеры. Проходить можно когда удобно,
          необязательно одновременно. Побеждает тот, кто ответит точнее и быстрее.
        </p>
        <button className="btn" onClick={create} disabled={busy}>
          ⚔️ {busy ? 'Создаю…' : 'Вызвать друга'}
        </button>
        {link && (
          <p className="small dim" style={{ marginBottom: 0 }}>
            Ссылка-вызов: <span style={{ wordBreak: 'break-all' }}>{link}</span>
          </p>
        )}
        {error && <p className="small" style={{ color: 'var(--critical)' }}>{error}</p>}
      </div>

      {active.length === 0 ? (
        <div className="empty">Дуэлей пока не было</div>
      ) : (
        active.map((d) => {
          const state = myStatus(d, meId);
          const mine = d.challenger.userId === meId ? d.challenger : d.opponent!;
          const other = d.challenger.userId === meId ? d.opponent : d.challenger;
          const won = d.winnerId === meId;

          return (
            <div className="card" key={d.id}>
              <div className="row between">
                <b>{other ? other.name : 'Ждём соперника'}</b>
                {d.status === 'finished' && (
                  <span className={won ? 'pill win' : d.winnerId === null ? 'pill' : 'pill lose'}>
                    {d.winnerId === null ? '🤝 ничья' : won ? '🏆 победа' : 'поражение'}
                  </span>
                )}
              </div>

              {d.status === 'finished' && other && (
                <table className="table-view" style={{ marginTop: 8 }}>
                  <tbody>
                    <tr>
                      <td>{mine.name} (ты)</td>
                      <td className="num">{mine.score} очков</td>
                      <td className="num">{mine.correct}/{mine.total}</td>
                    </tr>
                    <tr>
                      <td>{other.name}</td>
                      <td className="num">{other.score} очков</td>
                      <td className="num">{other.correct}/{other.total}</td>
                    </tr>
                  </tbody>
                </table>
              )}

              {state === 'my-turn' && (
                <button className="btn" style={{ marginTop: 10 }} onClick={() => onPlay(d.id)}>
                  Пройти свою половину
                </button>
              )}
              {state === 'waiting' && (
                <p className="small dim" style={{ marginBottom: 0 }}>
                  Ты уже отыграл — ждём соперника.
                </p>
              )}
              {state === 'no-rival' && (
                <p className="small dim" style={{ marginBottom: 0 }}>
                  Вызов ещё никто не принял. Перешли ссылку другу.
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
